import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import toast from 'react-hot-toast';
import client from '../../service/graphql';
import { IGraphQLResponse } from '../../types/commonTypes';
import {
  ICreateCheckoutSessionResponse,
  IdowngradeSubscriptionResponse,
  activateFreePlan,
  activateFreePlanFailure,
  activateFreePlanSuccess,
  activateFreeTrial,
  activateFreeTrialFailure,
  activateFreeTrialSuccess,
  createBillingPortalSession,
  createBillingPortalSessionFailure,
  createBillingPortalSessionSuccess,
  createCheckoutSession,
  createCheckoutSessionFailure,
  createCheckoutSessionSuccess,
  enableSubscription,
  enableSubscriptionFailure,
  enableSubscriptionSuccess,
  getSubscriptionPlansFailure,
  getSubscriptionPlansRequest,
  getSubscriptionPlansSuccess,
  unSubscribePlan,
  unSubscribePlanFailure,
  unSubscribePlanSuccess,
} from './subscriptionSlice';
import {
  CREATE_BILLING_PORTAL_SESSION,
  CREATE_CHECKOUT_SESSION,
  ENABLE_FREE_PLAN,
  ENABLE_FREE_TRIAL,
  ENABLE_SUBSCRIPTION,
  GET_SUBSCRIPTIONPLANS,
  UNSUBSCRIBE_PLAN,
} from '../../service/graphql/subscriptionQuery';
import { toastStyles } from '../../utils/utils';
import {
  fetchCoachRequest,
  updateSubscriptionPlanName,
  updateSubscriptionPlanStatus,
} from '../coach/coachSlice';

function* watchCreateCheckoutSession({
  payload,
}: PayloadAction<{ planType?: string; coachId: string }>) {
  try {
    const result: IGraphQLResponse<ICreateCheckoutSessionResponse> = yield call(
      client.query.bind(client),
      {
        query: CREATE_CHECKOUT_SESSION,
        variables: {
          createCheckoutSessionInput: { coachId: payload.coachId, planType: payload.planType },
        },
      }
    );
    yield put(createCheckoutSessionSuccess(result.data.createCheckoutSession));
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(createCheckoutSessionFailure());
    }
  }
}

function* watchGetBillingPortalSession({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    const result = yield call(client.query.bind(client), {
      query: CREATE_BILLING_PORTAL_SESSION,
      variables: { ...payload },
    });
    yield put(createBillingPortalSessionSuccess(result.data));
  } catch (error: unknown) {
    yield put(createBillingPortalSessionFailure());
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
    }
  }
}

function* watchEnableSubscription({
  payload,
}: PayloadAction<{ sessionId: string; coachId: string }>) {
  try {
    yield call(client.query.bind(client), {
      query: ENABLE_SUBSCRIPTION,
      variables: { ...payload },
    });
    yield put(enableSubscriptionSuccess());
    yield put(updateSubscriptionPlanName());
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(enableSubscriptionFailure());
    }
  }
}

function* watchUnsubscribePlan() {
  try {
    const result: IGraphQLResponse<IdowngradeSubscriptionResponse> = yield call(
      client.mutate.bind(client),
      {
        mutation: UNSUBSCRIBE_PLAN,
      }
    );
    yield put(unSubscribePlanSuccess());
    if (result.data.unsubscribePlan.statusCode === 200 && result.data.unsubscribePlan.success) {
      yield put(fetchCoachRequest());
    } else {
      toast.error(result.data.unsubscribePlan.message, toastStyles.error);
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error(error.message, toastStyles.error);
      yield put(unSubscribePlanFailure());
    }
  }
}

function* watchActivateFreeTrial({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    const response = yield call(client.mutate.bind(client), {
      mutation: ENABLE_FREE_TRIAL,
      variables: { ...payload },
    });
    if (response.data.activateFreeTrial.success) {
      yield put(activateFreeTrialSuccess());
    }
  } catch (error: unknown) {
    yield put(activateFreeTrialFailure());
  }
}

function* watchGetSubscriptionPlans({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    const response = yield call(client.query.bind(client), {
      query: GET_SUBSCRIPTIONPLANS,
      variables: { coachId: payload.coachId },
    });
    const data = response.data.getSubscriptionPlans;
    yield put(
      updateSubscriptionPlanStatus({
        subscriptionPlanStatus: data.subscriptionPlanStatus,
        clientCountStatus: data.clientLimitStatus,
      })
    );
    yield put(getSubscriptionPlansSuccess(data.subscriptionPlans));
  } catch (error: unknown) {
    if (error instanceof Error) {
      yield put(getSubscriptionPlansFailure(error.message));
    }
  }
}

function* watchActivateFreePlan({ payload }: PayloadAction<{ coachId: string }>) {
  try {
    const response = yield call(client.mutate.bind(client), {
      mutation: ENABLE_FREE_PLAN,
      variables: { ...payload },
    });
    if (response.data.activateFreePlan.success) {
      yield put(activateFreePlanSuccess());
      // yield put(fetchCoachRequest());
    }
  } catch (error: unknown) {
    yield put(activateFreePlanFailure());
  }
}

export function* watchSubscriptionSagas() {
  yield takeLatest(createCheckoutSession, watchCreateCheckoutSession);
  yield takeLatest(createBillingPortalSession, watchGetBillingPortalSession);
  yield takeLatest(enableSubscription, watchEnableSubscription);
  yield takeLatest(unSubscribePlan, watchUnsubscribePlan);
  yield takeLatest(activateFreeTrial, watchActivateFreeTrial);
  yield takeLatest(getSubscriptionPlansRequest, watchGetSubscriptionPlans);
  yield takeLatest(activateFreePlan, watchActivateFreePlan);
}

const subscriptionSagas = watchSubscriptionSagas;
export default subscriptionSagas;
